import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import PhotoUpload from "@/components/common/PhotoUpload";
import StatusBadge from "@/components/common/StatusBadge";
import DataTable from "@/components/common/DataTable";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ACCESS_TYPES = [
  { v: "supplier_receiving", l: "Supplier Barang Masuk" },
  { v: "delivery_out", l: "Truk PT Keluar (Kirim ke Customer)" },
  { v: "pt_return", l: "Truk PT Kembali (Selesai Kirim)" },
  { v: "tenant", l: "Penyewa Lahan / Tangki (Tenant)" },
  { v: "visitor", l: "Tamu / Kunjungan" },
];

const initial = {
  access_type: "supplier_receiving",
  reference_no: "",
  vehicle_no: "",
  driver_name: "",
  company: "",
  purpose: "",
  notes: "",
  photo_in: "",
};

export function GateInInner() {
  const [form, setForm] = useState(initial);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const load = () => api.get("/gate/logs").then((r) => setRows(r.data)).catch(()=>{});
  useAutoRefresh(load);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/gate/in", form);
      toast.success(`Gerbang masuk tercatat: ${form.vehicle_no}`);
      setForm(initial);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={onSubmit} data-testid="gate-in-form" className="xl:col-span-3 rounded-xl bg-white border border-slate-200 p-6 shadow-sm space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Jenis Akses" required>
              <Select value={form.access_type} onValueChange={set("access_type")}>
                <SelectTrigger data-testid="gate-in-access-type" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCESS_TYPES.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                <b>Supplier Barang Masuk</b>: truk supplier datang bongkar barang.{" "}
                <b>Truk PT Keluar</b>: truk internal mau kirim ke customer.{" "}
                <b>Truk PT Kembali</b>: truk internal balik lagi setelah selesai kirim.{" "}
                <b>Tenant</b>: penyewa lahan/tangki.{" "}
                <b>Tamu</b>: kunjungan biasa (tidak bawa/ambil barang).
              </p>
            </Field>
            <Field label="Nomor Referensi (PO / DO / SJ)">
              <Input data-testid="gate-in-ref" value={form.reference_no} onChange={set("reference_no")} placeholder="PO-2026-000123" />
            </Field>
            <Field label="Nomor Kendaraan" required>
              <Input data-testid="gate-in-vehicle" value={form.vehicle_no} onChange={set("vehicle_no")} placeholder="B 1234 XYZ" required />
            </Field>
            <Field label="Nama Driver" required>
              <Input data-testid="gate-in-driver" value={form.driver_name} onChange={set("driver_name")} placeholder="Nama lengkap" required />
            </Field>
            <Field label="Perusahaan / Supplier / Tenant" required>
              <Input data-testid="gate-in-company" value={form.company} onChange={set("company")} placeholder="PT Contoh Sejahtera" required />
            </Field>
            <Field label="Tujuan Masuk" required>
              <Input data-testid="gate-in-purpose" value={form.purpose} onChange={set("purpose")} placeholder="Bongkar CPO ke Tank 02" required />
            </Field>
          </div>

          <Field label="Catatan Security">
            <Textarea data-testid="gate-in-notes" value={form.notes} onChange={set("notes")} rows={2} placeholder="Kondisi kendaraan, kelengkapan dokumen, dll." />
          </Field>

          <PhotoUpload
            label="Bukti Foto Kendaraan Masuk"
            testid="gate-in-photo"
            value={form.photo_in}
            onChange={set("photo_in")}
          />

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setForm(initial)} data-testid="gate-in-reset">Reset</Button>
            <Button
              type="submit"
              data-testid="gate-in-submit"
              disabled={submitting}
              className="bg-sky-600 hover:bg-sky-700 text-white h-10 px-5"
            >
              {submitting ? "Menyimpan…" : "Simpan Gerbang Masuk"}
            </Button>
          </div>
        </form>

        {/* Right — currently in area */}
        <div className="xl:col-span-2 rounded-xl bg-white border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800">Kendaraan di Area</h3>
              <p className="text-[11px] text-slate-500">10 log terbaru</p>
            </div>
            <span className="text-[11px] font-mono px-2 py-1 rounded-md bg-sky-50 text-sky-800 border border-sky-100">
              {rows.filter((r)=>r.status==="in_area").length} aktif
            </span>
          </div>
          <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
            {rows.slice(0, 12).map((r) => (
              <div key={r.id} className="p-3 rounded-lg border border-slate-100 hover:border-sky-200 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-[13px] font-bold text-slate-900">{r.vehicle_no}</div>
                    <div className="text-[11px] text-slate-500 truncate">{r.company}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              </div>
            ))}
            {rows.length === 0 && (
              <div className="py-8 text-center text-xs text-slate-400">Belum ada catatan gerbang.</div>
            )}
          </div>
        </div>
      </div>
  );
}

export default function GateInPage() {
  return (
    <AppShell title="Gerbang Masuk" subtitle="Catat kendaraan yang masuk ke area pabrik">
      <GateInInner />
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
